// Where the assets list remembers the location scope a user picked.
//
// Drilling into a location and then navigating away used to throw the choice
// out — coming back re-ran the role-scope preselect and dumped the user at
// their default location again. The pick now lives in sessionStorage, so it
// survives page changes and reloads but not a new browser session, and logout
// clears it explicitly (see redux-store/auth/actions.js) so the next sign-in
// starts from the default scope.
//
// Keyed per organization: a super-admin switching tenants should not inherit
// the previous tenant's location.
//
// Every accessor is guarded — sessionStorage throws outright in some privacy
// modes, and a lost preference must never take the page down with it.

const KEY_PREFIX = "assets.locationScope.";

/** Sentinel for "the user deliberately cleared the filter" — distinct from
 *  "nothing stored yet", which is what lets the preselect run. */
export const ALL_LOCATIONS = "__all__";

const keyFor = (orgId) => `${KEY_PREFIX}${orgId}`;

/** Returns {id, name} | ALL_LOCATIONS | null (nothing remembered). */
export const readLocationScope = (orgId) => {
  if (!orgId) return null;
  try {
    const raw = sessionStorage.getItem(keyFor(orgId));
    if (!raw) return null;
    if (raw === ALL_LOCATIONS) return ALL_LOCATIONS;
    const parsed = JSON.parse(raw);
    return Number.isInteger(parsed?.id) && parsed.id > 0 ? parsed : null;
  } catch (_) {
    return null;
  }
};

/** `location` is {id, name}, or null for "cleared to all locations". */
export const writeLocationScope = (orgId, location) => {
  if (!orgId) return;
  try {
    sessionStorage.setItem(
      keyFor(orgId),
      location?.id
        ? JSON.stringify({ id: location.id, name: location.name || "" })
        : ALL_LOCATIONS
    );
  } catch (_) {
    // Preference lost, page unaffected.
  }
};

/** Called on logout: drop every org's remembered scope. */
export const clearAllLocationScopes = () => {
  try {
    Object.keys(sessionStorage)
      .filter((k) => k.startsWith(KEY_PREFIX))
      .forEach((k) => sessionStorage.removeItem(k));
  } catch (_) {
    // Nothing to clear, or storage unavailable.
  }
};
